import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material';
import { PixFavoredMenu } from '../../models/favored-menu.enum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PixPaymentKeyService } from '../payment-key.service';
import { PixPaymentFavored } from '../../models/payment-favored.model';
import { PixModalPaymentKeyFavoredCreateSuccessComponent } from './modal-create-success/modal-create-success.component';
import { PixModalPaymentKeyFavoredCreateNotFoundComponent } from './modal-create-not-found/modal-create-not-found.component';
import { Helper } from '../../../../shared/helper';
import { OmniValidators } from '../../../../shared/validators';
import { PixKeyType } from '../../models/key-type.enum';
import { PixKeysService } from '../../keys/keys.service';
import { PixPaymentFavoredView } from '../../models/payment-favored-view.model';
import { Util } from '../../../../utils/util';
import { TipoIniciacaoTransferencia } from '../../models/tipo-iniciacao-transacao.enum';

@Component({
	selector: 'app-pix-payment-key-favored-create',
	templateUrl: './favored-create.component.html',
	styleUrls: ['./favored-create.component.styl'],
	encapsulation: ViewEncapsulation.None
})
export class PixPaymentKeyFavoredCreateComponent implements OnInit {

	public selectedMenu: PixFavoredMenu = null;
	public form: FormGroup;

	constructor(
		private router: Router,
		public dialog: MatDialog,
		private formBuilder: FormBuilder,
		private pixKeysService: PixKeysService,
		private pixPaymentKeyService: PixPaymentKeyService
	) {}

	ngOnInit(): void {
		this.buildForm();
	}

	buildForm() {
		this.form = this.formBuilder.group({
			cpfCnpj: ['', Validators.required],
			email: [''],
			phone: [''],
			evp: [''],
		});
	}

	selectKeyType(type: PixFavoredMenu) {
		this.selectedMenu = type;
		this.form.reset();
		this.resetAndUpdateValidators();

		switch (type) {
			case PixFavoredMenu.PHONE:
				this.setRulesForPhoneNumber();
				break;
			case PixFavoredMenu.CPFCNPJ:
				this.setRulesForCpfCnpj();
				break;
			case PixFavoredMenu.EMAIL:
				this.setRulesForEmail();
				break;
			case PixFavoredMenu.EVP:
				this.setRulesForVirtualAddress();
				break;
			case PixFavoredMenu.ACCOUNT:
				this.setRulesForAccount();
				break;
		}
	}

	setRulesForAccount() {
		this.router.navigate(['pix/pagamento/favorecido/adicionar/conta']);
	}

	setRulesForCpfCnpj() {
		this.form.get('cpfCnpj').setValidators([Validators.required]);
		this.form.get('cpfCnpj').updateValueAndValidity();
	}

	setRulesForEmail() {
		this.form.get('email').setValidators([Validators.required, Validators.email]);
		this.form.get('email').updateValueAndValidity();
	}

	setRulesForPhoneNumber() {
		this.form.get('phone').setValidators([Validators.required, Validators.minLength(11)]);
		this.form.get('phone').updateValueAndValidity();
	}

	setRulesForVirtualAddress() {
		this.form.get('evp').setValidators([Validators.required]);
		this.form.get('evp').updateValueAndValidity();
	}

	resetAndUpdateValidators() {
		this.form.get('cpfCnpj').clearValidators();
		this.form.get('email').clearValidators();
		this.form.get('phone').clearValidators();
		this.form.get('evp').clearValidators();
		this.form.get('cpfCnpj').updateValueAndValidity();
		this.form.get('email').updateValueAndValidity();
		this.form.get('phone').updateValueAndValidity();
		this.form.get('evp').updateValueAndValidity();
	}

	submit() {
		if (this.form.valid) {
			switch (this.selectedMenu) {
				case PixFavoredMenu.CPFCNPJ:
					const cpfCpnj = this.form.get('cpfCnpj').value.replace(/\D/g, '');
					const type = cpfCpnj.length === 11 ? PixKeyType.CPF : PixKeyType.CNPJ;
					this.getUserKey(cpfCpnj, type);
					break;
				case PixFavoredMenu.PHONE:
					this.getUserKey(this.form.get('phone').value.replace(/\D/g, ''), PixKeyType.PHONE);
					break;
				case PixFavoredMenu.EMAIL:
					this.getUserKey(this.form.get('email').value, PixKeyType.EMAIL);
					break;
				case PixFavoredMenu.EVP:
					this.getUserKey(this.form.get('evp').value, PixKeyType.EVP);
					break;
			}
		}
	}

	getUserKey(key: string, keyType: PixKeyType) {
		key = keyType === PixKeyType.PHONE ? '+55' + key : key;

		this.pixKeysService.getAccountByCodeAndType(key, keyType).subscribe(
			result => {
				if (result && result.Existe && result.Sucesso) {
					const model = new PixPaymentFavoredView(Util.keysToCamel(result.Retorno) as PixPaymentFavored);
					model.tipoIniciacaoTransacao = TipoIniciacaoTransferencia.FAVORECIDO_NAO_CADASTRADO;
					this.pixPaymentKeyService.setSelectedPixPaymentFavoredView(model);
					this.openModalSuccess();
				} else {
					this.openModalFail();
				}
			},
			err => {
				if (err.status === 404) {
					this.openModalFail();
				}
			});
	}

	openModalSuccess() {
		const dialogRef = this.dialog.open(PixModalPaymentKeyFavoredCreateSuccessComponent, {
			width: '380px',
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.router.navigate(['pix/pagamento/favorecido/adicionar/valor-data']);
			}
		});
	}

	openModalFail() {
		this.dialog.open(PixModalPaymentKeyFavoredCreateNotFoundComponent, {
			width: '320px'
		});
	}

	cpfcnpjPaste(event) {
		event.preventDefault();
		const clipboardData = event.clipboardData;
		const pastedText = clipboardData.getData('text').trim().replace(/\D/g, '');
		this.setMaskAndValidatorToCpfCnpj(pastedText);
	}

	cpfcnpjMask(event) {
		if (!event.target.value) {
			return;
		}
		if (event.keyCode === 8) {
			return;
		}
		this.setMaskAndValidatorToCpfCnpj(event.target.value.replace(/\D/g, ''));
	}

	setMaskAndValidatorToCpfCnpj(value: string) {
		value = Helper.maskCpfCnpj(value);
		if (value.length <= 14) {
			this.form.controls.cpfCnpj.setValidators([Validators.required, Validators.minLength(14)]);
			this.form.controls.cpfCnpj.updateValueAndValidity();
		} else if (value.length > 14) {
			this.form.controls.cpfCnpj.setValidators([Validators.required, Validators.minLength(18), OmniValidators.isValidCpfCnpj]);
			this.form.controls.cpfCnpj.updateValueAndValidity();
		}
		this.form.controls.cpfCnpj.setValue(value);
	}

	public get pixFavoredMenu(): typeof PixFavoredMenu {
		return PixFavoredMenu;
	}

}



// WEBPACK FOOTER //
// /home/ubuntu/workdir/workspace/Front End Angular (NGINX)/omni-internetbanking/dev-omni-internetbanking-deploy/src/app/master/pix/payment-key/favored-create/favored-create.component.ts